/**
 * EXPLODE × a TOP-LEVEL heavy `POLYLINE` — the decomposition into `LINE` and
 * `ARC` members, and the refusal for the subtypes that decompose into something
 * else (@codex PR #785 round 14, FINDING 2).
 *
 * # The defect this closes
 *
 * `geometry/explode-entities` had a `case` for `LWPOLYLINE` and one for
 * `Block`, and NOTHING for the heavy `POLYLINE` the same PR had just made a
 * typed top-level primitive. A selection holding one therefore left it whole
 * while the command reported plain success — a silent no-op, which CLAUDE.md
 * ПРИОРИТЕТ #1 forbids just as firmly as a silent deletion. The sibling defect
 * one level down (a heavy POLYLINE *inside* a block definition, DELETED by
 * EXPLODE) is `./explodeBlockChildPolylineTableOle.ts`; this is the top-level
 * half, and it is a genuinely different question — there the entity is CARRIED
 * through the INSERT affine and stays a POLYLINE, here it CEASES TO EXIST and
 * is replaced by its members.
 *
 * # What AutoCAD does, per subtype
 *
 * Autodesk's EXPLODE command reference gives one row per heavy-polyline
 * subtype, and they are four different answers:
 *
 *   - **2D polyline** — "Discards any associated width or tangent information.
 *     For wide polylines, the resulting lines and arcs are placed along the
 *     center of the polyline." So: each span becomes a `LINE` when the span's
 *     bulge is 0 and an `ARC` when it is not, the closing span of a closed
 *     polyline included, and the per-vertex / entity-default WIDTHS (groups 40 /
 *     41) are DROPPED rather than carried. This is also exactly what ezdxf 1.4.4
 *     does: `ezdxf/render/polyline.py::virtual_polyline2d_entities` builds its
 *     span list as `(location.x, location.y, bulge)` triples — the widths are
 *     not even READ — and `_virtual_polyline_entities` writes `start`/`end` or
 *     `center`/`radius`/`start_angle`/`end_angle`, never a width. Verified
 *     against the installed ezdxf 1.4.4 rather than quoted from memory.
 *
 *     CURVE-FIT (group 70 bit 2) and SPLINE-FIT (bit 4) polylines are 2D
 *     polylines and take this same row: what explodes is the DRAWN run, which is
 *     precisely {@link heavyPolylineRuns}' own answer — every record for a
 *     curve-fit polyline (its generated bit-1 vertices lie ON the fitted curve
 *     and carry the bulges that bow it), and the generated bit-8 approximation
 *     records ALONE for a spline-fit one (the bit-16 records are the `SPLFRAME`
 *     control polygon, which is not part of the curve). "Discards tangent
 *     information" is the group-50 curve-fit tangent, which a LINE / ARC has no
 *     field for and which this decomposition therefore drops, as AutoCAD says.
 *
 *   - **3D polyline** — "Explodes into line segments." Every span is a `LINE`
 *     between two real WCS vertices; group 42 is a 2D-polyline field, so there
 *     are no arcs to find (ezdxf `virtual_polyline3d_entities` is the same
 *     walk).
 *
 *   - **polygon mesh / poly-face mesh** — "Explodes into 3D faces" (ezdxf:
 *     `virtual_polymesh_entities` / `virtual_polyface_entities`, both yielding
 *     `3DFACE`). This editor's `drawingEntitySchema` has NO `3DFACE` primitive
 *     — a mesh currently round-trips as a typed POLYLINE and draws through
 *     `polylineMeshWireRuns`, and inventing a LINE-wireframe stand-in for a
 *     FACE would be exactly the "placeholder instead of the real thing"
 *     ПРИОРИТЕТ #1 forbids (a 3DFACE is a filled, 4-corner, per-edge-visibility
 *     surface record, not four lines). So the two mesh subtypes DECLINE, wholly
 *     and VISIBLY, through the same refusal channel the `ACAD_TABLE` /
 *     `OLE2FRAME` explode refusals use, with their own bilingual audit row.
 *     Completing them needs a `3DFACE` primitive first — model, bridge,
 *     renderer and property editing — which is its own PR.
 *
 * A heavy POLYLINE whose visible run holds fewer than two records draws nothing
 * and can produce no member, so it declines too rather than being deleted into
 * nothing: EXPLODE must never remove an entity it did not replace.
 *
 * # One truth, not a second copy of the polyline
 *
 * Nothing about "which records are drawn, in what order, with which bulges,
 * and where are they in WCS" is re-derived here: that is
 * `cad-core/heavyPolylineTopology.ts` ({@link heavyPolylineRuns} /
 * {@link heavyPolylineWcsResolver}), the same module the renderer, the picker,
 * the PDF plot and the WCS bounds walk consume. The bulge → arc trigonometry is
 * `drawing-model/bulgeArc.ts` ({@link bulgeArc}), and the bulge-arc → DXF
 * CCW angle-pair swap is that module's {@link bulgeArcCcwAngles}, shared with
 * the block-child render explode. An exploded polyline can therefore never
 * disagree with the polyline it came from about where its own curve is.
 */

import {
  bulgeArc,
  bulgeArcCcwAngles,
  isDefaultExtrusion,
  polylineSubtype,
} from "@dxf-ai-editor/drawing-model";
import type {
  ArcEntity,
  DrawingEntity,
  LineEntity,
  PolylineEntity,
} from "@dxf-ai-editor/drawing-model";
import {
  heavyPolylineRuns,
  heavyPolylineWcsResolver,
} from "@dxf-ai-editor/cad-core";

/**
 * Why EXPLODE cannot decompose this heavy POLYLINE, or `null` when it can.
 *
 * `polygon-mesh` / `poly-face-mesh` — AutoCAD explodes these into `3DFACE`
 * records and this editor has no `3DFACE` primitive (module header).
 * `no-drawable-run` — the visible record run holds fewer than two records, so
 * there is no span to become a member and nothing for EXPLODE to replace the
 * entity WITH; declining keeps the record instead of deleting it into nothing.
 */
export type HeavyPolylineExplodeRefusal =
  | "polygon-mesh"
  | "poly-face-mesh"
  | "no-drawable-run";

/**
 * The ONE expressibility predicate for a top-level heavy-POLYLINE EXPLODE —
 * read by the document reducer (which keeps the entity) and by the shell
 * reducer (which reports the decline), exactly as
 * {@link import("./explodeBlockChildPolylineTableOle").explodeChildRefusal} is
 * for the block-child half. A single predicate is what makes it impossible for
 * the shell to report a refusal the document did not make, or to stay silent
 * about one it did.
 *
 * Total and pure. A 2D (including curve-fit and spline-fit) or 3D polyline with
 * a drawable run is always decomposable.
 */
export function heavyPolylineExplodeRefusal(
  entity: PolylineEntity,
): HeavyPolylineExplodeRefusal | null {
  const subtype = polylineSubtype(entity);
  if (subtype === "polygon-mesh") return "polygon-mesh";
  if (subtype === "polyface-mesh") return "poly-face-mesh";
  return drawableRuns(entity).length === 0 ? "no-drawable-run" : null;
}

/**
 * The visible runs that actually have a SPAN — the ONE answer both
 * {@link heavyPolylineExplodeRefusal} and {@link explodeHeavyPolylineMembers}
 * read, so "this polyline is declined" and "this polyline produced no member"
 * are the same sentence rather than two thresholds that can drift.
 *
 * The RUN — not `entity.vertices` — is what must hold two records: a spline-fit
 * polyline's run is its GENERATED approximation alone
 * (`cad-core/heavyPolylineVisibleIndices`), so a record array of twenty
 * control-frame points with one generated vertex draws a single point and
 * explodes to nothing.
 */
function drawableRuns(
  entity: PolylineEntity,
): ReturnType<typeof heavyPolylineRuns> {
  return heavyPolylineRuns(entity).filter((run) => run.indices.length >= 2);
}

/** How many heavy POLYLINEs of an EXPLODE dispatch each refusal reason claims. */
export interface HeavyPolylineExplodeRefusalSummary {
  readonly refusedPolygonMeshCount: number;
  readonly refusedPolyFaceMeshCount: number;
  readonly refusedNoDrawableRunCount: number;
  /** `true` when at least one selected heavy POLYLINE was left whole. */
  readonly refused: boolean;
}

/**
 * Count what a `geometry/explode-entities` dispatch will DECLINE among the
 * heavy POLYLINEs it targets, through {@link heavyPolylineExplodeRefusal}.
 *
 * Unlike the INSERT refusal, this one is PER-ENTITY rather than all-or-nothing:
 * a selection of five polylines of which one is a mesh explodes the other four
 * and keeps the mesh, which is what AutoCAD does with a mixed selection. The
 * summary therefore says how many were kept, not whether the command ran.
 */
export function summarizeHeavyPolylineExplodeRefusal(
  entities: readonly PolylineEntity[],
): HeavyPolylineExplodeRefusalSummary {
  let refusedPolygonMeshCount = 0;
  let refusedPolyFaceMeshCount = 0;
  let refusedNoDrawableRunCount = 0;
  for (const entity of entities) {
    switch (heavyPolylineExplodeRefusal(entity)) {
      case "polygon-mesh":
        refusedPolygonMeshCount += 1;
        break;
      case "poly-face-mesh":
        refusedPolyFaceMeshCount += 1;
        break;
      case "no-drawable-run":
        refusedNoDrawableRunCount += 1;
        break;
      // No `default:` arm — `heavyPolylineExplodeRefusal` returns `null` for a
      // decomposable polyline and falling out of the switch IS the "count
      // nothing" such an arm would write; an explicit `default: break;` is a
      // statement no input can distinguish from its absence (the same shape,
      // and the same targeted-mutation-testing reason, as
      // `summarizeExplodeInsertRefusal`).
    }
  }
  return {
    refusedPolygonMeshCount,
    refusedPolyFaceMeshCount,
    refusedNoDrawableRunCount,
    refused:
      refusedPolygonMeshCount > 0 ||
      refusedPolyFaceMeshCount > 0 ||
      refusedNoDrawableRunCount > 0,
  };
}

/**
 * The common AcDbEntity properties every member inherits from the polyline it
 * came from — AutoCAD's EXPLODE gives each resulting object the source's
 * layer / linetype / colour / lineweight / transparency (ezdxf spells the same
 * set `Polyline.graphic_properties()`), plus the DXF group 48 linetype scale
 * and the shared-object reference handles (347 material, 390 plot style) that a
 * copy may legitimately point at.
 *
 * DELIBERATELY ABSENT: `id` (the caller mints one per member), `ownerHandle`
 * (330) and `extensionDictionaryHandle` (360) — own-record identity the source
 * still holds, the same rule
 * `explodeBlockChildPolylineTableOle.stripExplodedChildHandles` states — and
 * `extraGroups`, which are the unmapped groups of a POLYLINE record and would
 * be emitted verbatim onto a LINE that never carried them.
 */
function memberBase(
  entity: PolylineEntity,
): Omit<LineEntity, "id" | "type" | "start" | "end"> {
  return {
    spaceId: entity.spaceId,
    layer: entity.layer,
    color: entity.color,
    linetype: entity.linetype,
    lineweight: entity.lineweight,
    transparency: entity.transparency,
    // DXF group 39. AutoCAD keeps an extruded polyline's thickness on the lines
    // and arcs it explodes into — both records carry the field.
    thickness: entity.thickness,
    ...(entity.linetypeScale !== undefined
      ? { linetypeScale: entity.linetypeScale }
      : {}),
    ...(entity.materialHandle !== undefined
      ? { materialHandle: entity.materialHandle }
      : {}),
    ...(entity.plotStyleHandle !== undefined
      ? { plotStyleHandle: entity.plotStyleHandle }
      : {}),
    ...(entity.visibility !== undefined
      ? { visibility: entity.visibility }
      : {}),
    ...(entity.shadowMode !== undefined
      ? { shadowMode: entity.shadowMode }
      : {}),
  };
}

/**
 * Decompose a top-level heavy `POLYLINE` into the `LINE` / `ARC` members
 * AutoCAD's EXPLODE leaves behind, or `[]` when
 * {@link heavyPolylineExplodeRefusal} declines it.
 *
 * `mintId` is the caller's id allocator (`"line"` / `"arc"` prefix, the same
 * prefixes `nextGeometryEntityId` uses), because the document — not the command
 * engine — owns the id counter.
 *
 * ## The OCS split, and why the two member kinds carry different frames
 *
 * A 2D heavy polyline's vertices are OCS coordinates in the plane of its group
 * 210 extrusion, at its own group-30 ELEVATION. DXF puts a `LINE`'s endpoints in
 * WCS and an `ARC`'s centre in OCS, so the two members must be written in
 * DIFFERENT frames — and that is exactly what ezdxf 1.4.4's
 * `_virtual_polyline_entities` does (`ocs.to_wcs(point)` for the line,
 * `Vec3(center.x, center.y, elevation)` plus a copied `extrusion` for the arc).
 * The WCS lift is {@link heavyPolylineWcsResolver}, so it is the same lift the
 * renderer, the picker and the bounds walk apply to the source polyline.
 *
 * One deliberate divergence from ezdxf, in the direction of keeping data:
 * ezdxf sets no `extrusion` on the exploded LINE, which silently loses the
 * direction a non-zero group-39 THICKNESS extrudes along. A LINE's 210 does not
 * move its WCS endpoints (it is the thickness / OCS-arbitrary-axis normal
 * only), so carrying it is lossless where dropping it is not.
 *
 * For a 3D polyline the vertices ARE WCS, no extrusion applies, and every span
 * is a straight LINE — group 42 is a 2D-polyline field.
 *
 * ## The arc's angle pair comes from `bulgeArc`, NOT from ezdxf's `bulge_to_arc`
 *
 * The two disagree about which side of the chord a POSITIVE bulge bows: for
 * `v1 = (0,0)`, `v2 = (20,0)`, `bulge = +1`, `drawing-model/bulgeArc.ts` puts the
 * apex at `(10, +10)` (its module doc names that as its pinned reference case)
 * while ezdxf 1.4.4 `bulge_to_arc` returns `start 180° / end 0°`, i.e. the apex
 * at `(10, −10)` (measured on the installed ezdxf, both through `bulge_to_arc`
 * and through `Path.flattening` of the equivalent LWPOLYLINE). That is a
 * REPO-WIDE convention question, not an EXPLODE one: `bulgeArc` is what the
 * renderer, the picker, the bounds walk, TRIM/EXTEND and the block-child
 * explode all draw a bulged span with, and re-deriving the angle pair from
 * ezdxf's rule HERE would make EXPLODE the single place where a polyline's arc
 * jumps to the other side of its chord. So this module is deliberately
 * consistent with the source polyline as this editor draws it, and the sign
 * convention itself is left to whoever settles it for the whole repo. Should it
 * ever be flipped in `bulgeArc`, this module follows automatically — it does no
 * bulge trigonometry of its own.
 */
export function explodeHeavyPolylineMembers(
  entity: PolylineEntity,
  mintId: (prefix: string) => string,
): DrawingEntity[] {
  if (heavyPolylineExplodeRefusal(entity) !== null) return [];

  const planar = polylineSubtype(entity) === "2d";
  const toWcs = heavyPolylineWcsResolver(entity);
  const base = memberBase(entity);
  // Group 210 rides onto both member kinds when it is non-default: on the ARC
  // it is REQUIRED (the centre above is an OCS point), on the LINE it preserves
  // the thickness direction. A 3D polyline / mesh has no OCS at all.
  const extrusion =
    planar && !isDefaultExtrusion(entity.extrusion) && entity.extrusion
      ? { extrusion: entity.extrusion }
      : {};
  // The elevation of the 2D plane — the POLYLINE's own group-30 (the "dummy
  // point" whose Z is the elevation), which is where a 2D arc's centre sits.
  const elevationZ = entity.elevation.z ?? 0;

  const members: DrawingEntity[] = [];
  // `drawableRuns`, not `heavyPolylineRuns`: the "has at least one span" filter
  // is the SAME call the refusal above made, so there is no second threshold
  // here to fall out of step with it — and no run reaching this loop can be
  // shorter than two records.
  for (const run of drawableRuns(entity)) {
    const count = run.indices.length;
    const spanCount = run.closed ? count : count - 1;
    for (let span = 0; span < spanCount; span += 1) {
      const fromIndex = run.indices[span]!;
      const toIndex = run.indices[(span + 1) % count]!;
      // OCS x/y — the frame the bulge lives in. `heavyPolylineRuns` already
      // zeroes the bulge for every subtype that cannot carry one, so this is
      // never a 3D polyline's phantom group 42.
      const fromOcs = entity.vertices[fromIndex]!.location;
      const toOcs = entity.vertices[toIndex]!.location;
      const arc = planar
        ? bulgeArc(
            { x: fromOcs.x, y: fromOcs.y },
            { x: toOcs.x, y: toOcs.y },
            run.bulges[span] ?? 0,
          )
        : null;
      if (arc && arc.radius > 0) {
        // `radius > 0` is ezdxf's own guard (`if radius > 0:` in
        // `_virtual_polyline_entities`) and the model's: `arcEntitySchema`
        // requires a POSITIVE radius, so a degenerate arc must fall through to
        // the chord rather than mint an unparseable ARC.
        //
        // targeted-cad-mutation-testing EQUIVALENT MUTANTS (verified
        // survivors): `ConditionalExpression` `arc.radius > 0` -> `true`, and
        // `EqualityOperator` `> 0` -> `>= 0`. Both are equivalent because of an
        // upstream invariant of `bulgeArc` (`drawing-model/bulgeArc.ts`): it
        // returns `null` unless `|bulge| >= 1e-12` AND the chord is `>= 1e-12`,
        // and it then computes `Math.abs(chord / (2·sin(θ/2)))` — an absolute
        // value of a non-zero numerator, so a non-null result's radius is
        // STRICTLY POSITIVE for every input `polylineVertexRecordSchema` admits
        // (zod's `z.number()` already rejects `NaN`, and `±Infinity` yields
        // `θ = ±2π`, whose `sin` is a tiny non-zero, hence a huge POSITIVE
        // radius). No input can therefore distinguish the three forms. The
        // guard is kept because it states the ARC record's own precondition
        // where the record is minted, not because the paths differ.
        const ccw = bulgeArcCcwAngles(arc);
        const arcMember: ArcEntity = {
          ...base,
          ...extrusion,
          id: mintId("arc"),
          type: "Arc",
          center: { x: arc.cx, y: arc.cy, z: elevationZ },
          radius: arc.radius,
          // Degrees inline rather than via a module-level `RAD_TO_DEG`: a
          // constant at module scope is a STATIC initializer, and this repo's
          // Stryker setup cannot attribute a test to one — a wrong factor in it
          // survives as an unattributable false negative instead of being
          // killed by the angle assertions that do cover it. Same form as
          // `scene3d/adapters/typed/blockBulgeExplode.ts`'s arc piece.
          startAngle: (ccw.startRad * 180) / Math.PI,
          endAngle: (ccw.endRad * 180) / Math.PI,
        };
        members.push(arcMember);
        continue;
      }
      // Straight — `bulgeArc` reporting `null` IS the repo's one-truth answer
      // to "does this span draw an arc?", and when it does not, the drawn path
      // of the span is its chord. (ezdxf's `radius > 0` branch drops a span
      // whose two vertices coincide entirely; emitting the zero-length LINE
      // instead matches AutoCAD, which is well known for leaving zero-length
      // lines behind when a polyline had a duplicated vertex, and it can never
      // lose drawn geometry.)
      const lineMember: LineEntity = {
        ...base,
        ...extrusion,
        id: mintId("line"),
        type: "Line",
        start: toWcs(fromIndex),
        end: toWcs(toIndex),
      };
      members.push(lineMember);
    }
  }
  return members;
}
